import { Button, Dialog, DialogContent, DialogProps, DialogTitle, Divider, IconButton } from '@material-ui/core';
import AddIcon from '@material-ui/icons/Add';
import ArrowBackIcon from '@material-ui/icons/ArrowBack';
import ArrowForwardIcon from '@material-ui/icons/ArrowForward';
import CloseIcon from '@material-ui/icons/Close';
import GetAppOutlinedIcon from '@material-ui/icons/GetAppOutlined';
import PermMediaOutlinedIcon from '@material-ui/icons/PermMediaOutlined';
import PlayCircleOutlineIcon from '@material-ui/icons/PlayCircleOutline';
import { FormSelectUserField } from 'app/main/settings/users/forms/FormSelectUserField';
import { useUsersData } from 'app/main/settings/users/UsersCacheContext';
import { Form, Formik, FormikHelpers, useFormikContext } from 'formik';
import { ChipX, ChipXProps } from 'framework/components/chips/ChipX';
import { ColorX } from 'framework/components/color/ColorX';
import { HtmlDivProps } from 'framework/components/HtmlDivProps';
import { IQueryContext } from 'framework/context/IQueryContext';
import { QueryProvider as FrameworkQueryProvider } from 'framework/context/QueryProvider';
import { CancelSubmitFormDialogActions } from 'framework/forms/CancelSubmitFormDialogActions';
import { FormSelectFieldOnId } from 'framework/forms/FormSelectFieldOnId';
import { FormTextField } from 'framework/forms/FormTextField';
import { handleFormResponse } from 'framework/forms/utils/handleFormResponse';
import { setFormValue } from 'framework/forms/utils/setFormValue';
import { useApiEffect } from 'framework/hooks/useApiEffect';
import { useFormSubmit } from 'framework/hooks/useFormSubmit';
import { useSnackbarNotify } from 'framework/hooks/useSnackbarNotify';
import { formatDate } from 'framework/utils/date/formatDate';
import {
	businessDataQuery_summary,
	CochlearPatientStatus,
	filterSortPageCochlearPatients_concurrencyToken,
	filterSortPageCochlearPatients_handle,
	getCochlearInfoEvents_handle,
	ICochlearInfoEventDto,
	ICochlearMediaAsset,
	ICochlearPatientsFilterSortPageParams,
	ICochlearPatientSummaryDto,
	ICochlearSettingsDto,
	IQueryResult,
	IRegisterAsACochlearSpecialistRequest,
	IRegisterAudiologistForCochlearEventRequest,
	IRegisterPatientForCochlearEventRequest,
	registerAsACochlearSpecialist_handle,
	registerAudiologistForCochlearEvent_handle,
	registerPatientForCochlearEvent_handle,
} from 'gen/ApiClient';
import PdfSvg from 'libs/assets/icons/pdf.svg';
import Fg1 from 'libs/ui/layout/fg1';
import ColumnStack, { ColumnStackProps } from 'libs/ui/layout/stack/column.stack';
import RowStack from 'libs/ui/layout/stack/row.stack';
import { IStrings } from 'localization/IStrings';
import { useLocalization } from 'localization/useLocalization';
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useHistory } from 'react-router-dom';
import { useDialogsContext } from 'shared/dialogs/useDialogsContext';
import { PatientSearchProvider } from 'shared/patientSearch/PatientSearchProvider';
import * as yup from 'yup';
import { cochlearPatientsQueryParamsLsKey } from '../../../localStorageKeys';
import { CochlearRoute } from '../IntegrationsRoutes';
import { useQueryContext } from 'framework/hooks/useQueryContext';

type CiIndicationChipProps = Omit<ChipXProps, 'label'> & {
	patientId: string;
	push?: boolean;
};

export const CiIndicationChip = ({ push, patientId, ...rest }: CiIndicationChipProps) => {
	const strings = useLocalization();
	const { push: pushHistory } = useHistory();
	const { setParams } = useQueryContext(CochlearPatientsQueryContext);

	const onPush = () => {
		setParams({ ...DefaultCochlearPatientsFilterSortPageParams, patientId });
		pushHistory(CochlearRoute);
	};

	return (
		<ChipX
			label={strings.ciIndication}
			color='warning'
			fill
			onClick={push === true ? () => onPush() : undefined}
			{...rest}
		/>
	);
};

type CiCandidateChipProps = Omit<ChipXProps, 'label'> & {
	patientId: string;
	push?: boolean;
};

export const CiCandidateChip = ({ push, patientId, ...rest }: CiCandidateChipProps) => {
	const strings = useLocalization();
	const { push: pushHistory } = useHistory();
	const { setParams } = useQueryContext(CochlearPatientsQueryContext);

	const onPush = () => {
		setParams({ ...DefaultCochlearPatientsFilterSortPageParams, patientId });
		pushHistory(CochlearRoute);
	};

	return (
		<ChipX
			label={strings.ciCandidate}
			color='warning'
			fill
			onClick={push === true ? () => onPush() : undefined}
			{...rest}
		/>
	);
};

export function createCochlearPatientStatusRecord(strings: IStrings): Record<CochlearPatientStatus, string> {
	return {
		BimodallyFitted: strings.bimodallyFitted,
		Implanted: strings.implanted,
		Informed: strings.informed,
		Referred: strings.referred,
		Registered: strings.registered,
	};
}

export const CochlearPatientStatusColorRecord: Record<CochlearPatientStatus, ColorX> = {
	BimodallyFitted: 'success',
	Implanted: 'primary',
	Informed: 'warning',
	Referred: 'warning',
	Registered: 'error',
};

type CochlearPatientStatusChipProps = Omit<ChipXProps, 'label'> & {
	status: CochlearPatientStatus;
};

export const CochlearPatientStatusChip = ({ status, ...rest }: CochlearPatientStatusChipProps) => {
	const strings = useLocalization();
	const record = createCochlearPatientStatusRecord(strings);

	return (
		<ChipX
			label={record[status]}
			color={CochlearPatientStatusColorRecord[status]}
			{...rest}
		/>
	);
};

export const CochlearPatientsQueryContext = React.createContext<IQueryContext<IQueryResult<ICochlearPatientSummaryDto>, ICochlearPatientsFilterSortPageParams>>(
	undefined as any
);

export const DefaultCochlearPatientsFilterSortPageParams: ICochlearPatientsFilterSortPageParams = {
	searchString: '',
	sortParams: { direction: 'asc', property: 'lastName' },
	pageParams: { index: 1, size: 10 },
	statuses: [],
	isCiCandidate: true,
	bimodallyFittedDateAfter: undefined,
	bimodallyFittedDateBefore: undefined,
	implantedDateAfter: undefined,
	implantedDateBefore: undefined,
	informedDateAfter: undefined,
	informedDateBefore: undefined,
	referralDateAfter: undefined,
	referralDateBefore: undefined,
	patientId: undefined,
};

export const CochlearPatientsQueryProvider = ({ children }: { children: any }) => {
	return (
		<FrameworkQueryProvider<IQueryResult<ICochlearPatientSummaryDto>, ICochlearPatientsFilterSortPageParams>
			defaultParams={DefaultCochlearPatientsFilterSortPageParams}
			queryF={filterSortPageCochlearPatients_handle}
			concurrencyF={filterSortPageCochlearPatients_concurrencyToken}
			localStorageKey={cochlearPatientsQueryParamsLsKey}
			context={CochlearPatientsQueryContext}>
			{children}
		</FrameworkQueryProvider>
	);
};

export const CochlearProviders = ({ children }: any) => {
	return <CochlearPatientsQueryProvider>{children}</CochlearPatientsQueryProvider>;
};

type MediaAssetsCarouselDialogProps = {
	assets: ICochlearMediaAsset[];
	close: VoidFunction;
};

export const MediaAssetsCarouselDialog = ({ assets, close }: MediaAssetsCarouselDialogProps) => {
	const strings = useLocalization();

	return (
		<Dialog
			onClose={close}
			open>
			<RowStack
				dfrowac
				gap={1}
				style={{ padding: '12px' }}>
				<div>{strings.images}</div>
				<Fg1 />
				<IconButton onClick={close}>
					<CloseIcon />
				</IconButton>
			</RowStack>
			<Divider />
			<MediaAssetsCarousel
				assets={assets}
				variant='dialog'
			/>
		</Dialog>
	);
};

type MediaAssetsCarouselProps = Omit<ColumnStackProps, 'children'> & {
	selectedId?: string;
	assets: ICochlearMediaAsset[];
	openDialog?: (id: string) => void;
	onClick?: (id: string) => void;
	variant: 'embedded' | 'dialog';
	close?: VoidFunction;
};

export const MediaAssetsCarousel = ({ assets, openDialog, close, variant, selectedId, ...rest }: MediaAssetsCarouselProps) => {
	const [selectedAssetId, setSelectedAssetId] = useState<string | undefined>(selectedId);
	const selectedAsset = assets.find(x => x.id === selectedAssetId);
	const isFirst = selectedAssetId === assets[0]?.id;
	const isLast = selectedAssetId === assets[assets.length - 1]?.id;
	const thumbnailAssets = useMemo(() => (variant === 'embedded' ? assets.slice(0, 5) : assets), [assets, variant]);
	const remainingAssetsCount = assets.length - thumbnailAssets.length;

	useEffect(() => {
		if (assets.length !== 0 && selectedAssetId === undefined) {
			setSelectedAssetId(assets[0].id);
		} else if (assets.find(x => x.id === selectedAssetId) === undefined) {
			setSelectedAssetId(undefined);
		}
	}, [assets, selectedAssetId]);

	const onNext = useCallback(() => {
		const isLast = selectedAssetId === assets[assets.length - 1]?.id;
		if (isLast) {
			setSelectedAssetId(assets[0].id);
			return;
		}
		const f = assets[assets.findIndex(x => x.id === selectedAssetId) + 1];
		if (f) {
			setSelectedAssetId(f.id);
		}
	}, [assets, selectedAssetId]);

	const onPrevious = useCallback(() => {
		const isFirst = selectedAssetId === assets[0]?.id;
		if (isFirst) {
			setSelectedAssetId(assets[assets.length - 1].id);
			return;
		}
		const f = assets[assets.findIndex(x => x.id === selectedAssetId) - 1];
		if (f) {
			setSelectedAssetId(f.id);
		}
	}, [assets, selectedAssetId]);

	useEffect(() => {
		if (assets.length <= 1 && variant === 'embedded') return;

		const handleKeyDown = (event: KeyboardEvent) => {
			if (event.key === 'ArrowLeft') {
				event.preventDefault();
				onPrevious();
			}

			if (event.key === 'ArrowRight') {
				event.preventDefault();
				onNext();
			}

			if (event.key === 'Escape') {
				close?.();
			}
		};

		window.addEventListener('keydown', handleKeyDown);

		return () => {
			window.removeEventListener('keydown', handleKeyDown);
		};
	}, [assets.length, close, onNext, onPrevious, variant]);

	return (
		<ColumnStack
			dfcolac
			style={{ gap: '16px' }}
			{...rest}>
			<Fg1 />
			{selectedAsset && (
				<>
					<MediaAssetCarousel
						item={selectedAsset}
						onClick={openDialog}
						onNext={isLast === true ? undefined : onNext}
						onPrevious={isFirst === true ? undefined : onPrevious}
						size={variant === 'dialog' ? 500 : 400}
					/>
				</>
			)}
			<Fg1 />
			<MediaAssetsThumbnailsStack
				selectedId={selectedAssetId}
				setSelectedId={setSelectedAssetId}
				remaining={
					remainingAssetsCount > 0 && (
						<ThumbnailBox
							onClick={() => openDialog?.(selectedAssetId!)}
							style={{ backgroundColor: 'gray' }}>
							<div style={{ fontSize: 18, color: 'gray' }}>{`+${remainingAssetsCount}`}</div>
						</ThumbnailBox>
					)
				}
				assets={thumbnailAssets}
			/>
		</ColumnStack>
	);
};

type MediaAssetCarouselProps = {
	item: ICochlearMediaAsset;
	onNext?: VoidFunction;
	onPrevious?: VoidFunction;
	onClick?: (id: string) => void;
	size: 400 | 500;
};

const MediaAssetCarousel = ({ item, onNext, onPrevious, onClick, size }: MediaAssetCarouselProps) => {
	return (
		<RowStack
			w100
			dfrowac>
			<IconButton
				disabled={onPrevious === undefined}
				onClick={onPrevious}>
				<ArrowBackIcon />
			</IconButton>
			<Fg1 />
			<MediaAssetViewBox
				item={item}
				style={{
					borderRadius: '16px',
					height: size,
					maxWidth: size,
					aspectRatio: '1/1',
					justifyContent: 'center',
					alignItems: 'center',
					display: 'flex',
					cursor: 'pointer',
				}}
				onClick={() => onClick?.(item.id)}
			/>
			<Fg1 />
			<IconButton
				disabled={onNext === undefined}
				onClick={onNext}>
				<ArrowForwardIcon />
			</IconButton>
		</RowStack>
	);
};

type MediaAssetViewBoxProps = Omit<HtmlDivProps, 'children'> & {
	item: ICochlearMediaAsset;
};

const MediaAssetViewBox = ({ item, ...rest }: MediaAssetViewBoxProps) => {
	return (
		<div {...rest}>
			{item.mediaType === 'Image' && (
				<img
					src={item.url}
					alt={item.id ?? 'Product image'}
					style={{
						maxWidth: '100%',
						maxHeight: '100%',
						objectFit: 'fill',
						borderRadius: '16px',
					}}
				/>
			)}
			{item.mediaType === 'Video' && (
				<iframe
					src={toYouTubeEmbedUrl(item.url!)}
					title='YouTube video'
					allow='accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share'
					allowFullScreen
					style={{
						width: '100%',
						height: '100%',
						border: 0,
						borderRadius: 8,
					}}
				/>
			)}
			{item.mediaType === 'Pdf' && (
				<ColumnStack
					dfcolac
					style={{ gap: '8px' }}>
					<PdfSvg style={{ width: '100px', height: '100px' }} />
					<Button
						color='primary'
						onClick={e => {
							e.stopPropagation();
							e.preventDefault();
							window.open(item.url, '_blank');
						}}
						startIcon={<GetAppOutlinedIcon />}>
						{`Download`}
					</Button>
				</ColumnStack>
			)}
			{item.mediaType === 'Unknown' && (
				<div
					title='unknown media'
					style={{
						width: '100%',
						height: '100%',
						border: 0,
						borderRadius: '16px',
						display: 'flex',
						alignItems: 'center',
						justifyContent: 'center',
					}}>
					<PermMediaOutlinedIcon style={{}} />
				</div>
			)}
		</div>
	);
};

function toYouTubeEmbedUrl(url: string): string {
	const uri = new URL(url);

	if (uri.hostname === 'youtu.be') {
		const videoId = uri.pathname.slice(1);
		return `https://www.youtube.com/embed/${videoId}`;
	}

	if (uri.hostname.includes('youtube.com')) {
		const videoId = uri.searchParams.get('v');

		if (videoId) {
			return `https://www.youtube.com/embed/${videoId}`;
		}

		if (uri.pathname.startsWith('/embed/')) {
			return url;
		}
	}

	return url;
}

type MediaAssetsThumbnailsStackProps = {
	assets: ICochlearMediaAsset[];
	selectedId?: string;
	setSelectedId: (id: string) => void;
	remaining?: React.ReactNode;
};

const MediaAssetsThumbnailsStack = ({ assets, selectedId, setSelectedId, remaining }: MediaAssetsThumbnailsStackProps) => {
	return (
		<RowStack
			dfrowwrap
			gap={1}
			w100>
			{assets.map(t => (
				<MediaAssetThumbnail
					key={t.id}
					item={t}
					isSelected={t.id === selectedId}
					onClick={() => setSelectedId(t.id)}
				/>
			))}
			{remaining && remaining}
		</RowStack>
	);
};

type MediaAssetThumbnailProps = {
	item: ICochlearMediaAsset;
	isSelected: boolean;
	onClick: VoidFunction;
};

const MediaAssetThumbnail = ({ item, isSelected, onClick }: MediaAssetThumbnailProps) => {
	return (
		<ThumbnailBox
			onClick={onClick}
			style={{
				...(isSelected === true && { border: '2px solid blue' }),
			}}>
			{item.mediaType === 'Image' && (
				<img
					src={item.thumbnailUrl}
					alt={item.id ?? 'Thumbnail'}
					style={{
						width: '100%',
						height: '100%',
						objectFit: 'contain',
						padding: '2px',
						borderRadius: '8px',
					}}
				/>
			)}
			{item.mediaType === 'Video' && (
				<div
					style={{
						width: '100%',
						height: '100%',
						display: 'flex',
						alignItems: 'center',
						justifyContent: 'center',
						fontSize: 12,
						opacity: 0.8,
					}}>
					<PlayCircleOutlineIcon style={{ color: 'blue' }} />
				</div>
			)}
			{item.mediaType === 'Pdf' && (
				<div
					style={{
						width: '100%',
						height: '100%',
						display: 'flex',
						alignItems: 'center',
						justifyContent: 'center',
						fontSize: 12,
						opacity: 0.8,
					}}>
					<PdfSvg />
				</div>
			)}
			{item.mediaType === 'Unknown' && (
				<div
					style={{
						width: '100%',
						height: '100%',
						display: 'flex',
						alignItems: 'center',
						justifyContent: 'center',
						fontSize: 12,
						opacity: 0.8,
					}}>
					<PermMediaOutlinedIcon style={{}} />
				</div>
			)}
		</ThumbnailBox>
	);
};

type ThumbnailBoxProps = HtmlDivProps & {};

const ThumbnailBox = ({ onClick, style, ...rest }: ThumbnailBoxProps) => {
	return (
		<div
			onClick={onClick}
			style={{
				position: 'relative',
				width: 56,
				height: 56,
				padding: 0,
				border: '1px solid gray',
				borderRadius: '8px',
				backgroundColor: 'background.paper',
				cursor: 'pointer',
				overflow: 'hidden',
				flexShrink: 0,
				...style,
			}}
			{...rest}
		/>
	);
};

const createIRegisterPatientForCochlearEventRequestSchema = (strings: IStrings) => {
	return yup
		.object<IRegisterPatientForCochlearEventRequest>({
			eventId: yup.string().required(),
			patientId: yup.string().required(),
			patientEmail: yup.string().required(),
			replyTo: yup.string().required(),
		})
		.defined();
};

type RegisterPatientForCochlearEventDialogProps = DialogProps & {
	patientId: string;
	eventId: string | undefined;
	confirm: VoidFunction;
	cancel: VoidFunction;
};

export const RegisterPatientForCochlearEventDialog = ({ patientId, eventId, confirm, cancel, ...rest }: RegisterPatientForCochlearEventDialogProps) => {
	const notify = useSnackbarNotify();
	const [submit, isSubmitting] = useFormSubmit(registerPatientForCochlearEvent_handle);
	const strings = useLocalization();

	const handleSubmit = async (values: IRegisterPatientForCochlearEventRequest, helpers: FormikHelpers<IRegisterPatientForCochlearEventRequest>) => {
		const r = await submit(values);
		if (handleFormResponse(r, helpers)) {
			notify(`Mail verzonden`);
			confirm();
		}
	};

	return (
		<Formik<IRegisterPatientForCochlearEventRequest>
			validateOnMount
			initialValues={{
				eventId: eventId ?? '',
				patientEmail: '',
				patientId: patientId ?? '',
				replyTo: '',
			}}
			validationSchema={createIRegisterPatientForCochlearEventRequestSchema(strings)}
			onSubmit={handleSubmit}>
			<Form>
				<PatientSearchProvider>
					<InnerRegisterPatientForCochlearEventDialog
						isSubmitting={isSubmitting}
						cancel={cancel}
						// fixPatient={patientId !== undefined}
						fixEvent={eventId !== undefined}
						{...rest}
					/>
				</PatientSearchProvider>
			</Form>
		</Formik>
	);
};

type InnerRegisterPatientForCochlearEventDialogProps = DialogProps & {
	isSubmitting: boolean;
	cancel: VoidFunction;
	fixEvent: boolean;
};

const InnerRegisterPatientForCochlearEventDialog = ({ isSubmitting, cancel, fixEvent, ...rest }: InnerRegisterPatientForCochlearEventDialogProps) => {
	const strings = useLocalization();

	const props = useFormikContext<IRegisterPatientForCochlearEventRequest>();
	const [bd] = useApiEffect(businessDataQuery_summary);

	// const fPatientId = props.values.patientId;
	// const [fPatient] = useApiEffectWithDefer(patientsQuery_single, fPatientId, isNotNullNorUndefined);
	const [infoEvents] = useApiEffect(getCochlearInfoEvents_handle);

	// useEffect(() => {
	// 	console.log('ifinrng event...');
	// 	if (fPatient) {
	// 		setFormValue<IRegisterPatientForCochlearEventRequest>('patientEmail', fPatient.email ?? '', props);
	// 	} else {
	// 		setFormValue<IRegisterPatientForCochlearEventRequest>('patientEmail', '', props);
	// 	}
	// 	// eslint-disable-next-line
	// }, [fPatient]);

	useEffect(() => {
		if (bd) {
			setFormValue<IRegisterPatientForCochlearEventRequest>('replyTo', bd.email, props);
		}
		// eslint-disable-next-line
	}, [bd]);

	return (
		<Dialog
			{...rest}
			fullWidth
			maxWidth='sm'>
			<DialogTitle>{`Registreer patiënt voor event`}</DialogTitle>
			<DialogContent
				className='df-col'
				dividers>
				{fixEvent !== true && (
					<FormSelectFieldOnId<IRegisterPatientForCochlearEventRequest, ICochlearInfoEventDto>
						name='eventId'
						label={`Evenement`}
						options={infoEvents ?? []}
						style={{ width: '100%' }}
						getKey={t => t.id}
						renderValue={option => <div>{`${option.title} (${formatDate(option.date)})`}</div>}
					/>
				)}
				{/* {fixPatient !== true && (
					<FormSelectPatientField<IRegisterPatientForCochlearEventRequest>
						name='patientId'
						label={strings.patient}
						required
					/>
				)} */}
				<FormTextField<IRegisterPatientForCochlearEventRequest>
					name='patientEmail'
					label={`${strings.email} ${strings.patient}`}
					required
				/>
				<FormTextField<IRegisterPatientForCochlearEventRequest>
					name='replyTo'
					label={`Reply to`}
					required
				/>
			</DialogContent>
			<CancelSubmitFormDialogActions
				submitText={strings.sendEmail}
				isSubmitting={isSubmitting}
				cancel={cancel}
			/>
		</Dialog>
	);
};

const createIRegisterAsACochlearSpecialistRequestSchema = (_: IStrings) => {
	return yup
		.object<IRegisterAsACochlearSpecialistRequest>({
			replyTo: yup.string().required(),
		})
		.defined();
};

type RegisterAsACochlearSpecialistDialogProps = DialogProps & {
	confirm: VoidFunction;
	cancel: VoidFunction;
};

export const RegisterAsACochlearSpecialistDialog = ({ confirm, cancel, ...rest }: RegisterAsACochlearSpecialistDialogProps) => {
	const notify = useSnackbarNotify();
	const [submit, isSubmitting] = useFormSubmit(registerAsACochlearSpecialist_handle);
	const strings = useLocalization();

	const handleSubmit = async (values: IRegisterAsACochlearSpecialistRequest, helpers: FormikHelpers<IRegisterAsACochlearSpecialistRequest>) => {
		const r = await submit(values);
		if (handleFormResponse(r, helpers)) {
			notify(`Mail verzonden`);
			confirm();
		}
	};

	return (
		<Formik<IRegisterAsACochlearSpecialistRequest>
			validateOnMount
			initialValues={{
				replyTo: '',
			}}
			validationSchema={createIRegisterAsACochlearSpecialistRequestSchema(strings)}
			onSubmit={handleSubmit}>
			<Form>
				<InnerRegisterAsACochlearSpecialistDialog
					{...rest}
					cancel={cancel}
					isSubmitting={isSubmitting}
				/>
			</Form>
		</Formik>
	);
};

type InnerRegisterAsACochlearSpecialistDialogProps = DialogProps & {
	cancel: VoidFunction;
	isSubmitting: boolean;
};

const InnerRegisterAsACochlearSpecialistDialog = ({ cancel, isSubmitting, ...rest }: InnerRegisterAsACochlearSpecialistDialogProps) => {
	const strings = useLocalization();
	const props = useFormikContext<IRegisterAsACochlearSpecialistRequest>();
	const [bd] = useApiEffect(businessDataQuery_summary);

	useEffect(() => {
		if (bd) {
			setFormValue<IRegisterAsACochlearSpecialistRequest>('replyTo', bd.email, props);
		}
		// eslint-disable-next-line
	}, [bd]);

	return (
		<Dialog
			{...rest}
			fullWidth
			maxWidth='sm'>
			<DialogTitle>{strings.becomeSpecialist}</DialogTitle>
			<DialogContent
				className='df-col'
				dividers>
				<FormTextField<IRegisterAsACochlearSpecialistRequest>
					name='replyTo'
					label={`Reply to`}
					required
				/>
			</DialogContent>
			<CancelSubmitFormDialogActions
				submitText={strings.sendEmail}
				isSubmitting={isSubmitting}
				cancel={cancel}
			/>
		</Dialog>
	);
};

const createIRegisterAudiologistForCochlearEventRequestSchema = (strings: IStrings) => {
	return yup
		.object<IRegisterAudiologistForCochlearEventRequest>({
			eventId: yup.string().required(),
			replyTo: yup.string().required(),
			audiologistEmail: yup.string().required(),
			audiologistId: yup.string().required(),
		})
		.defined();
};

type RegisterAudiologistForCochlearEventDialogProps = DialogProps & {
	eventId: string | undefined;
	confirm: VoidFunction;
	cancel: VoidFunction;
};

export const RegisterAudiologistForCochlearEventDialog = ({ eventId, confirm, cancel, ...rest }: RegisterAudiologistForCochlearEventDialogProps) => {
	const notify = useSnackbarNotify();
	const [submit, isSubmitting] = useFormSubmit(registerAudiologistForCochlearEvent_handle);
	const strings = useLocalization();

	const handleSubmit = async (values: IRegisterAudiologistForCochlearEventRequest, helpers: FormikHelpers<IRegisterAudiologistForCochlearEventRequest>) => {
		const r = await submit(values);
		if (handleFormResponse(r, helpers)) {
			notify(`Mail verzonden`);
			confirm();
		}
	};

	return (
		<Formik<IRegisterAudiologistForCochlearEventRequest>
			validateOnMount
			initialValues={{
				eventId: eventId ?? '',
				audiologistEmail: '',
				audiologistId: '',
				replyTo: '',
			}}
			validationSchema={createIRegisterAudiologistForCochlearEventRequestSchema(strings)}
			onSubmit={handleSubmit}>
			<Form>
				<PatientSearchProvider>
					<InnerRegisterAudiologistForCochlearEventDialog
						isSubmitting={isSubmitting}
						cancel={cancel}
						fixEvent={eventId !== undefined}
						{...rest}
					/>
				</PatientSearchProvider>
			</Form>
		</Formik>
	);
};

type InnerRegisterAudiologistForCochlearEventDialogProps = DialogProps & {
	isSubmitting: boolean;
	cancel: VoidFunction;
	fixEvent: boolean;
};

const InnerRegisterAudiologistForCochlearEventDialog = ({ isSubmitting, cancel, fixEvent, ...rest }: InnerRegisterAudiologistForCochlearEventDialogProps) => {
	const strings = useLocalization();

	const props = useFormikContext<IRegisterAudiologistForCochlearEventRequest>();
	const [bd] = useApiEffect(businessDataQuery_summary);

	const [infoEvents] = useApiEffect(getCochlearInfoEvents_handle);

	const users = useUsersData({ includeBlocked: false, onlyAudiologists: false });
	const userId = props.values.audiologistId;
	const fUser = useMemo(() => users.find(t => t.id === userId), [users, userId]);
	const fUserEmail = useMemo(() => fUser?.email, [fUser]);

	useEffect(() => {
		if (fUserEmail !== props.values.audiologistEmail) {
			setFormValue<IRegisterAudiologistForCochlearEventRequest>('audiologistEmail', fUserEmail ?? '', props);
		}
		// eslint-disable-next-line
	}, [fUserEmail, props.values.audiologistEmail]);

	useEffect(() => {
		if (bd) {
			setFormValue<IRegisterAudiologistForCochlearEventRequest>('replyTo', bd.email, props);
		}
		// eslint-disable-next-line
	}, [bd]);

	return (
		<Dialog
			{...rest}
			fullWidth
			maxWidth='sm'>
			<DialogTitle>{strings.enrollAudiologistForAnEvent}</DialogTitle>
			<DialogContent
				className='df-col'
				dividers>
				{fixEvent !== true && (
					<FormSelectFieldOnId<IRegisterAudiologistForCochlearEventRequest, ICochlearInfoEventDto>
						name='eventId'
						label={`Evenement`}
						options={infoEvents ?? []}
						style={{ width: '100%' }}
						getKey={t => t.id}
						renderValue={option => <div>{`${option.title} (${formatDate(option.date)})`}</div>}
					/>
				)}
				<FormSelectUserField<IRegisterAudiologistForCochlearEventRequest>
					name='audiologistId'
					label={strings.audiologist}
				/>
				<FormTextField<IRegisterAudiologistForCochlearEventRequest>
					name='audiologistEmail'
					label={`${strings.email} ${strings.audiologist}`}
					required
				/>
				<FormTextField<IRegisterAudiologistForCochlearEventRequest>
					name='replyTo'
					label={`Reply to`}
					required
				/>
			</DialogContent>
			<CancelSubmitFormDialogActions
				submitText={strings.sendEmail}
				isSubmitting={isSubmitting}
				cancel={cancel}
			/>
		</Dialog>
	);
};

type RegisterAsCochlearSpecialistButtonProps = {
	settings: ICochlearSettingsDto;
};

export const RegisterAsCochlearSpecialistButton = ({ settings }: RegisterAsCochlearSpecialistButtonProps) => {
	const strings = useLocalization();
	const { open, confirm, cancel } = useDialogsContext();

	const onRegisterAsACochlearSpecialist = () => {
		open(
			<RegisterAsACochlearSpecialistDialog
				open
				confirm={confirm}
				cancel={cancel}
			/>
		);
	};

	return (
		<Button
			variant='contained'
			startIcon={<AddIcon />}
			size='small'
			color='primary'
			onClick={onRegisterAsACochlearSpecialist}
			disabled={settings.isPartner === true}>
			{strings.becomeSpecialist}
		</Button>
	);
};
